import { useState, useEffect, useMemo } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Sidebar from '../components/shared/sidebar';
import TemplateCard from '../components/template-card';
import SkeletonLoader from '../components/skeleton-loader';
import CustomSelect from '../components/custom-select';
import useTemplateManifest from '../hooks/use-template-manifest';
import { getFavorites, toggleFavorite } from '../utils/favorites';
import { highlightText } from '../utils/highlight';
import { TEMPLATE_CATEGORIES, SORT_OPTIONS} from '../constants/pages';
import useInsertPattern from '../hooks/use-insert-pattern';
import { sortTemplates } from '../utils/sort-templates';

const TemplatesPage = ({ onInsertComplete ,isProActive, handleUpgradeClick}) => {
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [searchQuery, setSearchQuery] = useState('');
	const [favorites, setFavorites] = useState([]);
	const [error, setError] = useState(null);
	const [sortBy, setSortBy] = useState('default');

	const {
		templates,
		loading,
		hasMore,
		loadMore,
		total
	} = useTemplateManifest('patterns', null, searchQuery, setError);

	useEffect(() => {
		getFavorites().then(setFavorites);
	}, []);

	const insertPattern = useInsertPattern(onInsertComplete);

	const normalizeCategory = (value) => String(value).trim().toLowerCase();

	// ✅ Combined filter + sort in one useMemo
	const visibleTemplates = useMemo(() => {
		const currentCategory = normalizeCategory(selectedCategory);
		const seenIds = new Set();

		const filtered = templates.filter((tpl) => {
			const categories = Array.isArray(tpl.category)
				? tpl.category.map(normalizeCategory)
				: [normalizeCategory(tpl.category || 'uncategorized')];

			if (currentCategory !== 'all' && !categories.includes(currentCategory)) {
				return false;
			}

			if (seenIds.has(tpl.id)) return false;
			seenIds.add(tpl.id);
			return true;
		});

		return sortTemplates(filtered, sortBy);
	}, [templates, selectedCategory, sortBy]);

	const handleToggleFavorite = async (id) => {
		const updated = await toggleFavorite(id);
		setFavorites(updated);
	};

	const renderError = () => (
		<p className="vayu-error-state">{error}</p>
	);

	const renderEmpty = () => (
		<p className={`vayu-empty-state ${searchQuery ? 'has-query' : ''}`}>
			{searchQuery ? (
				<>
					<span className="vayu-empty-state__icon">🔍</span>
					{__('No results found for', 'vayu-blocks')}
					<span className="vayu-empty-state__highlight">"{searchQuery}"</span>
				</>
			) : (
				<>
					<span className="vayu-empty-state__icon">📁</span>
					{__('No templates available here yet.', 'vayu-blocks')}
				</>
			)}
		</p>
	);

	const renderTemplates = () => (
		<>
			<div className="vayu-template-toolbar">
				{total > 0 && selectedCategory === 'all' && (
					<div className="vayu-dl-template-count">
						{`Showing ${visibleTemplates.length} of ${total} templates`}
					</div>
				)}

				{visibleTemplates.length > 1 && (
					<CustomSelect
						value={sortBy}
						onChange={setSortBy}
						options={SORT_OPTIONS}
					/>
				)}
			</div>

			<div className={`vayu-template-grid ${visibleTemplates.length > 0 ? 'fade-in-ready' : ''}`}>
				{visibleTemplates.map((tpl, index) => (
					<TemplateCard
						key={tpl.id}
						title={
							<span
								dangerouslySetInnerHTML={{
									__html: highlightText(tpl.title, searchQuery),
								}}
							/>
						}
						style={{ '--index': index }}
						preview={tpl.preview}
						premium={tpl.premium}
						pattern={tpl?.pattern || false}
						isFavorite={favorites.includes(tpl.id)}
						onClick={() => insertPattern(tpl.pattern, tpl.premium)}
						onToggleFavorite={() => handleToggleFavorite(tpl.id)}
						isProActive={isProActive}
						handleUpgradeClick={handleUpgradeClick}
					/>
				))}
			</div>

			{hasMore && selectedCategory === 'all' && total > templates.length && (
				<div className="vayu-load-more-wrap">
					<button
						className="vayu-load-more-button"
						onClick={loadMore}
						disabled={loading}
					>
						{loading ? __('Loading...', 'vayu-blocks') : __('Load More', 'vayu-blocks')}
					</button>
				</div>
			)}
		</>
	);

	return (
		<>
			<Sidebar
				activeCategory={selectedCategory}
				onSelectCategory={setSelectedCategory}
				onSearch={setSearchQuery}
				tab="patterns"
				categories={TEMPLATE_CATEGORIES['patterns']}
			/>

			<section className="vayu-main-content">
				{loading && templates.length === 0
					? <SkeletonLoader count={6} />
					: error
					? renderError()
					: visibleTemplates.length === 0
					? renderEmpty()
					: renderTemplates()}
			</section>
		</>
	);
};

export default TemplatesPage;
